"use strict";
// Copyright (c) Microsoft Corporation. All rights reserved.
var _a;
Object.defineProperty(exports, "__esModule", { value: true });
exports.NavigationKeyManager = void 0;
const consolePrefix = '[NavigationKeyManager]';
class KeyRequestTracker {
    constructor() {
        this.requests = new Map();
        this.requestorKeys = new Set();
        /** Associates a requestor with desired keyboard keys */
        this.set = (keys, requestorKey) => {
            this.delete(requestorKey);
            this.requestorKeys.add(requestorKey);
            for (const key of keys) {
                if (!this.requests.has(key)) {
                    this.requests.set(key, new Set());
                }
                const keyRequests = this.requests.get(key);
                keyRequests.add(requestorKey);
            }
        };
        /** Deletes the requestor from key requests */
        this.delete = (requestorKey) => {
            if (this.requestorKeys.delete(requestorKey)) {
                this.requests.forEach((set) => set.delete(requestorKey));
            }
        };
        /** Deletes all key requestors */
        this.clear = () => {
            this.requests.clear();
            this.requestorKeys.clear();
        };
        this.getRequestedKeys = () => {
            if (this.requestorKeys.size === 0) {
                return undefined;
            }
            const keys = [];
            for (const key of this.requests.keys()) {
                if (this.requests.get(key).size > 0) {
                    keys.push(key);
                }
            }
            return keys;
        };
    }
}
/**
 * The NavigationKeyManager is responsible for managing keyboard navigation keys across
 * different components within an application. It tracks requests for specific keyboard keys
 * from various components, computes the active set of keyboard keys based on these requests,
 * and supports dynamic updates to the keyboard navigation configuration.
 */
class NavigationKeyManager {
    /**
     * Resets the manager to its initial state, removing all requests for keyboard keys.
     * The active set of keyboard keys reverts to the default keys.
     */
    static reset() {
        this.requestTracker.clear();
        // Return to the default keys
        this.setKeys(this.defaultKeys);
        console.info(`${consolePrefix} Reset successful`);
    }
}
exports.NavigationKeyManager = NavigationKeyManager;
_a = NavigationKeyManager;
NavigationKeyManager.requestTracker = new KeyRequestTracker();
// The default keys to use when no requests are present
NavigationKeyManager.defaultKeys = [];
// The currently active set of keyboard keys
NavigationKeyManager.keyboardKeys = _a.defaultKeys;
// The currently active set of keyboard key strings
NavigationKeyManager.keyboardKeyStrings = new Set();
/**
 * Requests the registration of specific keyboard navigation keys for a component identified by a
 * unique key. Updates the active set of keyboard keys based on the union of all requests.
 *
 * If the requesting component's set of keys changes, the previously requested keys are replaced,
 * and the active keyboard keys are recalculated to reflect the current requests from all components.
 *
 * @param requestorKey A unique identifier for the requesting component.
 * @param keys The keyboard keys requested by the component.
 * @returns The updated active set of keyboard keys after processing the request.
 */
NavigationKeyManager.requestKeys = (requestorKey, keys) => {
    _a.requestTracker.set(keys, requestorKey);
    const newKeys = _a.getComputedKeys();
    _a.setKeys(newKeys);
    return newKeys;
};
/**
 * Revokes previously requested keyboard keys for a component, identified by its unique requestor key.
 * Recalculates the active set of keyboard keys, considering the remaining requests. If no requests
 * remain, reverts to the default set of keyboard keys.
 *
 * @param requestorKey The unique identifier for the component whose request is being revoked.
 * @returns The recalculated active set of keyboard keys after the revocation.
 */
NavigationKeyManager.releaseKeys = (requestorKey) => {
    _a.requestTracker.delete(requestorKey);
    const newKeys = _a.getComputedKeys();
    _a.setKeys(newKeys);
    return newKeys;
};
/**
 * Retrieves the currently active set of keyboard keys.
 *
 * @returns The current active set of keyboard keys.
 */
NavigationKeyManager.getKeys = () => {
    return [..._a.keyboardKeys];
};
/**
 * Checks if a specific keyboard key string is currently active.
 *
 * @param keyString The keyboard key string to check.
 * @returns true if the keyboard key string is actively requested, false otherwise.
 */
NavigationKeyManager.hasKey = (keyString) => {
    return _a.keyboardKeyStrings.has(keyString);
};
/**
 * Sets the default keyboard keys and recalculates the active set based on current requests.
 *
 * @param keys The new default set of keyboard keys.
 */
NavigationKeyManager.setDefaultKeys = (keys) => {
    _a.defaultKeys = keys;
    _a.setKeys(_a.getComputedKeys());
};
/**
 * Retrieves the default set of keyboard keys.
 *
 * @returns The default set of keyboard keys.
 */
NavigationKeyManager.getDefaultKeys = () => {
    return [..._a.defaultKeys];
};
/**
 * Updates the active set of keyboard keys.
 *
 * @param keys The new set of active keyboard keys.
 */
NavigationKeyManager.setKeys = (keys) => {
    _a.keyboardKeys = keys;
    _a.updateKeyStrings();
};
/**
 * Calculates the active set of keyboard keys based on the current requests from all components.
 * Defaults to the default keys if no requests are present.
 *
 * @returns The computed active set of keyboard keys.
 */
NavigationKeyManager.getComputedKeys = () => {
    const keys = _a.requestTracker.getRequestedKeys();
    if (!keys) {
        return _a.defaultKeys;
    }
    return keys;
};
/**
 * Updates the set of keyboard key strings based on the current active set of keyboard keys.
 */
NavigationKeyManager.updateKeyStrings = () => {
    _a.keyboardKeyStrings = new Set(_a.keyboardKeys.map((key) => key.toString()));
};
//# sourceMappingURL=data:application/json;base64,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